import type { DefaultError, UseMutationResult } from '@tanstack/react-query';
import type { ButtonProps } from '../Button/index.ts';
import type { DialogControlProps, DialogProps } from './Dialog.tsx';
import clsx from 'clsx';
import { isPlainObject } from 'es-toolkit';
import React, { useEffect, useRef } from 'react';
import { PiCheckBold, PiWarningBold } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { buttonCommonI18n } from '@_/i18n-common';
import { Button } from '../Button/index.ts';
import { Heading } from '../Heading/index.ts';
import { Dialog } from './Dialog.tsx';

export type ActionDialogProps = DialogControlProps &
  Pick<DialogProps, 'loading'> &
  React.PropsWithChildren<{
    heading?: React.ReactNode;
    mutation?: UseMutationResult<unknown, DefaultError, void>;
    primaryAction: {
      label: React.ReactNode;
    } & Pick<ButtonProps, 'disabled' | 'loading' | 'onClick'>;
    secondaryAction?:
      | boolean
      | ({
          label?: React.ReactNode;
        } & Pick<ButtonProps, 'disabled' | 'onClick'>);
    type?: 'default' | 'danger' | 'success';
  }>;

export function ActionDialog({
  children,
  heading,
  mutation,
  onOpenChange,
  primaryAction,
  secondaryAction,
  type = 'default',
  ...dialogProps
}: ActionDialogProps) {
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  // Automatically close dialog when mutation is successful
  useEffect(() => {
    if (mutation?.isSuccess) {
      onOpenChangeRef.current?.(false);
    }
  }, [mutation?.isSuccess]);

  let submit = primaryAction.onClick;
  if (!submit && mutation?.mutateAsync) {
    submit = async () => mutation.mutateAsync();
  }

  return (
    <Dialog
      {...dialogProps}
      disablePointerDismissal={mutation?.isPending || primaryAction?.loading}
      onOpenChange={onOpenChange}
      showSwipeHandle
    >
      {type !== 'default' && (
        <div
          className={clsx(
            'mx-auto flex size-12 items-center justify-center rounded-full',
            type === 'danger' && 'bg-red-100',
            type === 'success' && 'bg-emerald-100'
          )}
        >
          {type === 'danger' && <PiWarningBold className="size-5 text-red-600" />}
          {type === 'success' && <PiCheckBold className="size-5 text-emerald-600" />}
        </div>
      )}

      <div className={clsx('text-center', type === 'default' ? 'mt-1 sm:mt-3' : 'mt-3 sm:mt-5')}>
        {!!heading && (
          <Heading className="leading-6" level={4}>
            {heading}
          </Heading>
        )}
        <div className="mt-2 text-sm text-gray-500">{children}</div>
      </div>

      <div
        className={clsx(
          'mt-5 flex flex-col gap-2 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:gap-3',
          secondaryAction ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
        )}
      >
        <div className={clsx(secondaryAction && 'sm:col-start-2')}>
          <Button
            color={type === 'danger' ? 'danger' : 'primary'}
            disabled={primaryAction.disabled || mutation?.isSuccess}
            fillContainer
            loading={primaryAction.loading || mutation?.isPending}
            onClick={submit}
          >
            {primaryAction.label}
          </Button>
        </div>

        {!!secondaryAction && (
          <div className="sm:col-start-1">
            <Button
              disabled={
                (isPlainObject(secondaryAction) && secondaryAction.disabled) ||
                mutation?.isPending ||
                mutation?.isSuccess
              }
              fillContainer
              onClick={() => onOpenChange(false)}
            >
              {(isPlainObject(secondaryAction) && secondaryAction.label) || (
                <FormattedMessage {...buttonCommonI18n.cancel} />
              )}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
